// The office review of subcontractor documents (Session AUZ): every document a subcontractor SUBMITTED on the portal (or
// the office uploaded) and has not yet reviewed, every one not accepted and not yet replaced, every one expired or
// expiring, the required insurance not on file, and each linked engineer with no NVQ Level 3 on file. Derived live by
// GET /api/subcontractors/certificates/review; nothing is stored. Approve and Reject act on the row in place through the
// same routes the subcontractor page uses. Shared components throughout (PageHeader, ListToolbar, StatusPill, Button,
// Loader, lib/format dates): nothing hand-typed, no left stripe, no dashes.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type SubcontractorDocumentReview, type SubcontractorReviewItem, type SubcontractorReviewItemKind } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { formatDate } from "../../lib/format";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

/** Which registry entry an item's kind reads as (the registry owns the words and tones; nothing is styled here). */
function registryEntryFor(kind: SubcontractorReviewItemKind): { domain: "documentVerification" | "certificate"; status: string } {
  switch (kind) {
    case "awaiting_review":
      return { domain: "documentVerification", status: "submitted" };
    case "not_accepted":
      return { domain: "documentVerification", status: "rejected" };
    case "expired":
      return { domain: "certificate", status: "expired" };
    case "expiring":
      return { domain: "certificate", status: "expiring" };
    default:
      return { domain: "documentVerification", status: "unverified" };
  }
}

type Row = SubcontractorReviewItem & { subcontractorId: string; subcontractorName: string; statusLabel: string };

export function SubcontractorDocumentReviewPage() {
  const { data, error, retry } = useOptionsLoader<SubcontractorDocumentReview>(() => api.subcontractors.documentReview(), []);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [busyDoc, setBusyDoc] = useState<string | null>(null);
  // Below md the items render as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const rows = useMemo<Row[]>(
    () => (data?.subcontractors ?? []).flatMap((s) => s.items.map((i) => ({ ...i, subcontractorId: s.subcontractorId, subcontractorName: s.subcontractorName, statusLabel: s.statusLabel }))),
    [data],
  );
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.subcontractorName} ${r.label} ${r.engineerName ?? ""} ${r.kindLabel}`.toLowerCase().includes(q));
  }, [rows, search]);

  async function approve(row: Row) {
    if (!row.docId) return;
    setBusyDoc(row.docId);
    setActionError(null);
    try {
      const res = await api.subcontractors.approveCertificate(row.subcontractorId, row.docId);
      setNotice(`${row.subcontractorName}: ${row.label} approved. ${res.note}`);
      retry();
    } catch (e) {
      setActionError(friendlyApiError(e, "Could not approve the document."));
    } finally {
      setBusyDoc(null);
    }
  }

  // The Approve and Reject controls of one item, written once for the table row and the mobile card.
  const rowActions = (r: Row) =>
    r.docId && (r.kind === "awaiting_review" || r.kind === "not_accepted" || r.kind === "expiring") ? (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="primary" loading={busyDoc === r.docId} onClick={() => approve(r)}>
          Approve
        </Button>
        {r.kind !== "not_accepted" && (
          <Button size="sm" variant="secondary" onClick={() => setRejecting(r)}>
            Reject
          </Button>
        )}
      </div>
    ) : null;

  const counts = data?.counts;
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6">
      <PageHeader
        className="mb-4"
        title="Subcontractor documents"
        subtitle="Documents submitted for review, not accepted, expired or missing. Nothing counts as compliant, or as evidence of insurance, until the office approves it."
        actions={
          <Link to="/office/subcontractors" className="font-menu text-xs uppercase tracking-wide text-ele-blue hover:underline">
            Back to subcontractors
          </Link>
        }
      />
      {counts && (
        <p className="mb-3 text-sm text-ele-body">
          {counts.awaitingReview} awaiting review, {counts.notAccepted} not accepted, {counts.expired} expired, {counts.missing} required and not on file, {counts.expiring} expiring soon.
          Checked {data ? formatDate(data.checkedAt) : ""}.
        </p>
      )}
      <ListToolbar
        className="mb-3"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search company, document, engineer"
        resultCount={{ shown: shown.length, total: rows.length, noun: "items" }}
        onClear={() => setSearch("")}
      />
      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {actionError}
        </p>
      )}
      {error && data == null ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load the review list.{" "}
          <button type="button" onClick={retry} className="underline hover:text-ele-strong">
            Retry
          </button>
        </p>
      ) : data == null ? (
        <Loader label="Loading the documents..." />
      ) : shown.length === 0 ? (
        <p className="text-sm text-ele-body">{rows.length === 0 ? "Nothing to review: every subcontractor's documents are approved and current." : "Nothing matches that search."}</p>
      ) : mobileCards ? (
        /* Below md the five-column table cannot hold the Approve and Reject buttons beside the
           names. Each item is a card: the document leads, the company and its state follow, and the
           buttons sit on their own row. The card opens the subcontractor, as the company name does. */
        <MobileCardList label="Documents to review">
          {shown.map((r, i) => {
            const pill = registryEntryFor(r.kind);
            return (
              <MobileListCard
                key={`${r.subcontractorId}-${r.docId ?? r.kind}-${r.engineerName ?? ""}-${i}`}
                to={`/office/subcontractors/${encodeURIComponent(r.subcontractorId)}`}
                primary={r.label}
                secondary={
                  <>
                    {r.subcontractorName}
                    <span className="block text-xs opacity-70">{r.statusLabel}</span>
                  </>
                }
                meta={
                  <>
                    <span className="block">
                      {r.kindLabel}
                      {r.since && r.kind === "awaiting_review" ? `, submitted ${formatDate(r.since)}` : ""}
                      {r.kind === "not_accepted" && r.rejectionReason ? `: ${r.rejectionReason}` : ""}
                    </span>
                    <span className="block">{r.expiryDate ? `Expiry ${formatDate(r.expiryDate)}` : "No expiry"}</span>
                  </>
                }
                right={<StatusPill domain={pill.domain} status={pill.status} />}
                actions={rowActions(r) ?? undefined}
              />
            );
          })}
        </MobileCardList>
      ) : (
        <div data-safetynet className="overflow-x-auto">
          <table className="ele-list-table w-full text-left text-sm">
            <thead className="font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <th className="px-2 py-1">Subcontractor</th>
                <th className="px-2 py-1">Document</th>
                <th className="max-md:hidden px-2 py-1">State</th>
                <th className="max-md:hidden px-2 py-1">Expiry</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => {
                const pill = registryEntryFor(r.kind);
                return (
                  <tr key={`${r.subcontractorId}-${r.docId ?? r.kind}-${r.engineerName ?? ""}-${i}`} className="border-t border-ele-purple-accent/60">
                    <td className="px-2 py-1.5">
                      <Link to={`/office/subcontractors/${encodeURIComponent(r.subcontractorId)}`} className="text-ele-strong hover:underline">
                        {r.subcontractorName}
                      </Link>
                      <span className="block text-xs text-ele-body">{r.statusLabel}</span>
                    </td>
                    <td className="px-2 py-1.5 text-ele-strong">
                      {r.label}
                      <span className="block text-xs text-ele-body">
                        {r.kindLabel}
                        {r.since && r.kind === "awaiting_review" ? `, submitted ${formatDate(r.since)}` : ""}
                        {r.kind === "not_accepted" && r.rejectionReason ? `: ${r.rejectionReason}` : ""}
                      </span>
                    </td>
                    <td className="max-md:hidden px-2 py-1.5">
                      <StatusPill domain={pill.domain} status={pill.status} />
                    </td>
                    <td className="max-md:hidden px-2 py-1.5 text-ele-body">{r.expiryDate ? formatDate(r.expiryDate) : "No expiry"}</td>
                    <td className="px-2 py-1.5">{rowActions(r)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {rejecting && (
        <RejectDocumentModal
          subcontractorName={rejecting.subcontractorName}
          label={rejecting.label}
          onClose={() => setRejecting(null)}
          onConfirm={async (reason) => {
            await api.subcontractors.rejectCertificate(rejecting.subcontractorId, rejecting.docId!, reason);
            setNotice(`${rejecting.subcontractorName}: ${rejecting.label} marked not accepted. The subcontractor sees the reason on the portal.`);
            setRejecting(null);
            retry();
          }}
        />
      )}
    </div>
  );
}

/** The reason is required: the subcontractor reads it on the portal and uploads a replacement. Shared by both office pages. */
export function RejectDocumentModal({ subcontractorName, label, onClose, onConfirm }: { subcontractorName: string; label: string; onClose: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit() {
    if (!reason.trim()) {
      setErr("Say why the document is not accepted. The subcontractor will read it.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(reason.trim());
    } catch (e) {
      setErr(friendlyApiError(e, "Could not record the rejection."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalShell ariaLabel="Document not accepted" onClose={onClose} width="max-w-[520px]">
      <h2 className="mb-2 font-heading text-2xl tracking-wide text-ele-strong">Not accepted</h2>
      <p className="mb-3 text-sm text-ele-body">
        {subcontractorName}: {label}. Say why, in words the subcontractor will read on the portal, so they can upload a replacement. The document then counts
        for nothing until a replacement is approved.
      </p>
      <label className="text-sm text-ele-body">
        Reason
        <textarea
          className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {err && (
        <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {err}
        </p>
      )}
      <div className={MODAL_ACTIONS}>
        <Button variant="secondary" onClick={onClose} className={MODAL_ACTION_BTN}>
          Cancel
        </Button>
        <Button variant="destructive" loading={busy} onClick={submit} className={MODAL_ACTION_BTN}>
          Mark not accepted
        </Button>
      </div>
    </ModalShell>
  );
}
